import { Injectable } from '@angular/core';
import { Observable, finalize, map, of, shareReplay, tap, throwError } from 'rxjs';
import { Store } from '@ngrx/store';
import { AppState } from '../app.state';
import { refreshPermissions } from '../store/permissions/permissions.actions';
import { AuthService } from 'src/app/features/auth/authentication/services/auth.service';
import { LocalStorageService } from 'src/app/shared/localstorage.service';

/**
 * There is no session to work with — no access token and nothing to renew with.
 *
 * Distinct from a failed renewal, which is usually a network blip worth retrying.
 * This one is terminal: a caller should give up rather than loop.
 */
export class NoSessionError extends Error {
  constructor() {
    super('No access token');
    this.name = 'NoSessionError';
  }
}

/**
 * Renew the access token before it expires, for every caller in the app.
 *
 * This lives in its own service rather than inside `TokenInterceptor` because the
 * interceptor is not the only thing holding a token: the SSE stream authenticates
 * with the same access token, in its URL, and cannot go through an interceptor at
 * all. Both must share ONE in-flight refresh — the server rotates the refresh token
 * on every use and treats a replayed one as theft, so two independent refreshes
 * would revoke the session outright.
 */
@Injectable({
  providedIn: 'root',
})
export class TokenRefreshService {
  private refreshRequest$: Observable<string> | null = null;

  constructor(
    private readonly authService: AuthService,
    private readonly store: Store<AppState>
  ) {}

  /**
   * The stored access token when it still has useful life left, otherwise a fresh one.
   *
   * Callers that cannot react to a 401 — the SSE connection above all — must ask here
   * rather than reading `localStorage` directly, which is what left the stream retrying
   * a long-dead token until it gave up.
   */
  getValidAccessToken(): Observable<string> {
    const currentUser = LocalStorageService.get('currentUser');
    const accessToken: string | undefined = currentUser?.access_token;

    if (accessToken && !this.isExpiringSoon(accessToken)) {
      return of(accessToken);
    }

    if (!currentUser?.refresh_token) {
      // Nothing to renew with: a pre-rollout session, or signed out entirely.
      return accessToken ? of(accessToken) : throwError(() => new NoSessionError());
    }

    return this.refreshAccessToken();
  }

  /**
   * Exchange the refresh token for a new access token.
   *
   * De-duplicated: concurrent callers join the request already in flight instead of
   * each consuming the rotating refresh token.
   */
  refreshAccessToken(): Observable<string> {
    if (this.refreshRequest$) {
      return this.refreshRequest$;
    }

    const currentUser = LocalStorageService.get('currentUser');
    if (!currentUser?.refresh_token) {
      return throwError(() => new Error('Missing refresh token'));
    }

    this.refreshRequest$ = this.authService.refreshAccessToken(currentUser.refresh_token).pipe(
      map((response) => response.data?.[0]),
      tap((tokens) => {
        if (!tokens?.access_token || !tokens?.refresh_token) {
          throw new Error('Incomplete refresh response');
        }

        // Store both halves: the server rotates the refresh token on every use
        // and treats a replay as theft, so keeping the old value would revoke
        // the session on the next refresh.
        LocalStorageService.put('currentUser', {
          ...currentUser,
          access_token: tokens.access_token,
          refresh_token: tokens.refresh_token,
          token_type: tokens.token_type,
          expires_in: tokens.expires_in,
        });
        this.store.dispatch(refreshPermissions());
      }),
      map((tokens) => tokens!.access_token),
      finalize(() => {
        this.refreshRequest$ = null;
      }),
      shareReplay(1)
    );

    return this.refreshRequest$;
  }

  /**
   * True when *token* is expired, or close enough that handing it out is pointless.
   *
   * A token whose shape cannot be read is treated as expiring: renewing an
   * already-good token costs one request, whereas trusting an unreadable one
   * reproduces the bug this service exists to fix.
   */
  private isExpiringSoon(token: string): boolean {
    const expiresAt = this.readExpiry(token);
    if (expiresAt == null) {
      return true;
    }
    return expiresAt - Date.now() <= EXPIRY_SKEW_MS;
  }

  /** Epoch milliseconds from the JWT's `exp` claim, or null if it cannot be read. */
  private readExpiry(token: string): number | null {
    const payload = token.split('.')[1];
    if (!payload) {
      return null;
    }

    try {
      const base64 = payload.replace(/-/g, '+').replace(/_/g, '/');
      const claims = JSON.parse(atob(base64)) as { exp?: number };
      return typeof claims.exp === 'number' ? claims.exp * 1000 : null;
    } catch {
      return null;
    }
  }
}

/**
 * Renew this long before the token actually expires.
 *
 * Covers clock skew between browser and server plus the round trip itself, so a
 * connection opened on a "valid" token does not expire between the check and the
 * server reading it.
 */
const EXPIRY_SKEW_MS = 30_000;
